import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ErrorDialogComponent } from '../../shared/components/error-dialog/error-dialog';
import { UserService } from '../../core/services/user.service';
import { MatButtonModule } from '@angular/material/button';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { LucideAngularModule } from 'lucide-angular';
import { RegisterAccessComponent } from './components/register_access/register_access';
import { RegisterAddressComponent } from './components/register-address/register-address';
import { RegisterPersonalInfoComponent } from './components/register-personal-info/register-personal-info';
import { RegisterRenaperComponent } from './components/register_renaper/register_renaper';
import { RegisterSuccessComponent } from './components/register_success/register_success';

@Component({
	selector: 'app-register',
	standalone: true,
	imports: [
		CommonModule,
		MatStepperModule,
		MatButtonModule,
		LucideAngularModule,
		RegisterRenaperComponent,
		RegisterPersonalInfoComponent,
		RegisterAddressComponent,
		RegisterAccessComponent,
		RegisterSuccessComponent
	],
	templateUrl: './register.html',
	styleUrl: './register.scss'
})
export class RegisterComponent implements OnInit {
	private userService = inject(UserService);
	private dialog = inject(MatDialog);
	private route = inject(ActivatedRoute);

	@ViewChild('stepper') stepper!: MatStepper;
	@ViewChild('accessStep') accessComponent!: RegisterAccessComponent;
	@ViewChild('addressStep') addressComponent!: RegisterAddressComponent;

	// Estado del flujo
	registroCompletado = signal(false);
	emailRegistrado = signal('');
	autenticarError = signal(false);

	// Datos por paso
	renaperData = signal<any | null>(null);
	personalInfoData = signal<any | null>(null);
	addressData = signal<any | null>(null);

	// Signals de control para sub-componentes
	requestPersonalInfo = signal(false);
	requestAddresses = signal(false);

	// Estado de completado por paso
	step1Completed = signal(false);
	step2Completed = signal(false);
	step3Completed = signal(false);

	ngOnInit(): void {
		const userinfo = this.route.snapshot.queryParamMap.get('userinfo');
		const hasParams = this.route.snapshot.queryParamMap.keys.length > 0;

		if (userinfo) {
			const payload = this.parseJwt(userinfo);
			if (!payload) {
				this.dialog.open(ErrorDialogComponent, { width: '400px', data: 'Token de validación inválido.' });
				this.autenticarError.set(true);
			} else if (Date.now() / 1000 > payload.exp) {
				this.dialog.open(ErrorDialogComponent, { width: '400px', data: 'El token de validación expiró. Por favor, intentá nuevamente.' });
				this.autenticarError.set(true);
			} else if (payload.user_info?.proveedor?.toLowerCase() !== 'renaper') {
				this.dialog.open(ErrorDialogComponent, { width: '400px', data: 'El proveedor de validación no es válido.' });
				this.autenticarError.set(true);
			} else {
				const info = payload.user_info;
				this.onRenaperValidated({
					cuil: info.cuit ?? '',
					username: info.preferred_username ?? info.cuit ?? '',
					nombre: info.given_name ?? '',
					apellido: info.family_name ?? '',
					tipoDeDocumento: 'DNI',
					numeroDocumento: info.dni ?? '',
					sexo: '',
					genero: '',
					fechaDeNacimiento: null,
					nacionalidad: ''
				});
			}
		} else if (hasParams) {
			this.autenticarError.set(true);
		}
	}

	private parseJwt(token: string): any | null {
		const parts = token.split('.');
		if (parts.length !== 3) return null;
		try {
			return JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
		} catch {
			return null;
		}
	}

	// ─── Paso 1: Autenticar ────────────────────────────────────────────────────

	onRenaperValidated(data: any): void {
		this.renaperData.set(data);
		this.step1Completed.set(true);
		setTimeout(() => this.stepper.next());
	}

	// ─── Paso 2: Datos personales ─────────────────────────────────────────────

	onPersonalInfoChange(data: any): void {
		this.personalInfoData.set(data);

		if (this.stepper?.selectedIndex === 1) {
			this.step2Completed.set(true);
			setTimeout(() => this.stepper.next());
		}
	}

	tryNextFromPersonalInfo(): void {
		this.requestPersonalInfo.set(false);
		setTimeout(() => this.requestPersonalInfo.set(true));
	}

	// ─── Paso 3: Dirección ────────────────────────────────────────────────────

	onAddressChange(data: any): void {
		this.addressData.set(data);

		if (this.stepper?.selectedIndex === 2) {
			this.step3Completed.set(true);
			setTimeout(() => this.stepper.next());
		}
	}

	tryNextFromAddress(): void {
		this.requestAddresses.set(false);
		setTimeout(() => this.requestAddresses.set(true));
	}

	// ─── Paso 4: Datos de contacto + submit final ────────────────────────────

	handleFinalSubmit(): void {
		this.accessComponent.submit();
	}

	onSubmitRegister(): void {
		const personalInfo = this.personalInfoData();
		const address = this.addressData();
		const { email, celular, telefono } = this.accessComponent?.accessForm?.value ?? {};

		const fechaDeNacimiento = (() => {
			const val = personalInfo?.fechaDeNacimiento;
			if (!val) return null;
			if (typeof val === 'string') return val;
			const d = val as Date;
			return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
		})();

		const payload: any = {
			username: personalInfo?.username,
			nombre: personalInfo?.nombre,
			apellido: personalInfo?.apellido,
			tipoDeDocumento: personalInfo?.tipoDeDocumento,
			numeroDocumento: personalInfo?.numeroDocumento,
			fechaDeNacimiento,
			sexo: personalInfo?.sexo,
			genero: this.renaperData()?.genero || personalInfo?.sexo,
			nacionalidad: personalInfo?.nacionalidad,
			correoElectronico: email,
			nivelValidacion: 1,
			origenValidacion: 'ReNaPer',
			domicilios: address
				? [
						{
							tipoDomicilio: Number(address.tipoDomicilio),
							calle: address.calle,
							numero: address.numero,
							depto: address.depto ?? '',
							piso: address.piso ?? '',
							torre: address.torre ?? '',
							cp: address.cp,
							localidad: address.localidad,
							partido: address.partido
						}
					]
				: []
		};

		if (celular) payload.celular = celular;
		if (telefono) payload.telefono = telefono;

		this.userService.createVecino(payload).subscribe({
			next: () => {
				this.accessComponent?.setLoading(false);
				this.emailRegistrado.set(email ?? '');
				this.registroCompletado.set(true);
			},
			error: (err) => {
				this.accessComponent?.setLoading(false);
				const msg = err?.error?.error ?? err?.error?.message ?? 'Error al registrar. Intente nuevamente.';
				this.dialog.open(ErrorDialogComponent, { width: '400px', data: msg });
			}
		});
	}
}
